import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { useCallback, useEffect, useState } from 'react';

import type { PartBotLocation } from '@frontend/api';

import { calloutAuthorButton } from '../consts';
import { convertRectToPartBotLocation } from '../geomUtils';

export type CalloutAuthorInfo = {
  pageNumber: number;
  loc: PartBotLocation;
  annot: Core.Annotations.RectangleAnnotation;
};

type UseCalloutAuthorProps = {
  instance: WebViewerInstance | null;
  url: string | null;
  extractionsEnabled: boolean;
};

export const useCalloutAuthor = ({
  instance,
  url,
  extractionsEnabled,
}: UseCalloutAuthorProps) => {
  const [calloutAuthorInfo, setCalloutAuthorInfo] =
    useState<CalloutAuthorInfo | null>(null);

  useEffect(() => {
    if (!instance || !extractionsEnabled) return;

    const viewer = instance.Core.documentViewer;
    const annotationManager = viewer.getAnnotationManager();

    // Create the rectangle tool
    const rectangleTool = new instance.Core.Tools.RectangleCreateTool(viewer);
    rectangleTool.setStyles({
      StrokeColor: new instance.Core.Annotations.Color(140, 65, 237, 0.05),
      FillColor: new instance.Core.Annotations.Color(140, 65, 237, 0.15),
    });

    // Register the tool if it hasn't been registered yet
    instance.UI.registerTool({
      toolName: 'CalloutAuthor',
      toolObject: rectangleTool,
      buttonImage: 'icon-circle',
      tooltip: 'Add missing extraction',
    });

    // Get custom tool's element to manually toggle active and inactive button state
    const calloutAuthorButtonElement = document.getElementsByClassName(
      'calloutAuthorButton',
    );

    // This will listen for any tool mode changes and update the custom button's active state accordingly
    // PDFTron does not do this for free for custom buttons
    viewer.addEventListener('toolModeUpdated', (newTool) => {
      if (calloutAuthorButtonElement && calloutAuthorButtonElement[0]) {
        if (newTool.name === 'CalloutAuthor') {
          calloutAuthorButtonElement[0].classList.add('active');
        } else {
          calloutAuthorButtonElement[0].classList.remove('active');
        }
      }
    });

    // Create the Modular UI toolButton for the toolbar
    // Get the default header
    const defaultHeader = instance.UI.getModularHeader('default-top-header');

    // Create the button configuration
    const buttonConfig = new instance.UI.Components.ToolButton({
      dataElement: 'calloutAuthorButton',
      toolName: 'CalloutAuthor',
      img: calloutAuthorButton,
      className: 'calloutAuthorButton',
    });

    // Add the button directly to the header's items
    const currentItems = defaultHeader.getItems();
    defaultHeader.setItems([...currentItems, buttonConfig]);

    const handleAnnotationSelection = (
      annotations: Core.Annotations.RectangleAnnotation[] | unknown[],
      action: string,
    ) => {
      // Prevents the handler from being called again on annotation deletion
      if (action !== 'add') {
        return;
      }

      const annot = annotations[0];
      if (
        !(annot instanceof instance.Core.Annotations.RectangleAnnotation) ||
        annot.ToolName !== 'CalloutAuthor' // Ensures that the annotation is from the custom tool
      ) {
        return;
      }

      // turn off existing annotations
      // NOTE: not reaching into existing state to prevent re-renders
      const existingAnnotations = annotationManager
        .getAnnotationsList()
        .filter(
          (qAnnot) =>
            qAnnot.ToolName === 'CalloutAuthor' && qAnnot.Id !== annot.Id,
        );
      if (existingAnnotations.length > 0) {
        annotationManager.deleteAnnotations(existingAnnotations);
      }

      const pageNumber = annot.PageNumber;
      const rect = annot.getRect();
      const pageHeight = viewer.getPageHeight(pageNumber);

      annotationManager.setAnnotationStyles(annot, {
        FillColor: new instance.Core.Annotations.Color(0, 0, 0, 0),
        StrokeColor: new instance.Core.Annotations.Color(140, 65, 237, 1),
        StrokeThickness: 2,
        BorderRadius: 24,
        Style: 'dash',
        Dashes: '5, 5',
      });

      setCalloutAuthorInfo({
        pageNumber,
        loc: convertRectToPartBotLocation(rect, pageHeight),
        annot,
      });

      instance.UI.setToolMode('Pan');
    };

    // Listen for annotation creation
    annotationManager.addEventListener(
      'annotationChanged',
      handleAnnotationSelection,
    );

    return () => {
      annotationManager.removeEventListener(
        'annotationChanged',
        handleAnnotationSelection,
      );
    };
  }, [instance, extractionsEnabled]);

  const handleCloseCalloutAuthor = useCallback(() => {
    if (calloutAuthorInfo && instance) {
      const viewer = instance.Core.documentViewer;
      const annotationManager = viewer.getAnnotationManager();
      annotationManager.deleteAnnotation(calloutAuthorInfo.annot);
    }
    setCalloutAuthorInfo(null);
  }, [calloutAuthorInfo, instance]);

  useEffect(() => {
    // when the url changes turn off the tool
    setCalloutAuthorInfo(null);
  }, [url]);

  return { calloutAuthorInfo, handleCloseCalloutAuthor };
};
